/**
 * Manual del Ingeniero(a).
 * Verified behavior (V17+):
 *  - GASTOS: somete recibos como cualquier rol. Al someter su PROPIO recibo en
 *    un solo paso ("Enviar Reporte"), este omite el Nivel 1 (su propia revisión)
 *    y va directo a Revisión Final (receiptController createReceipt: engineer →
 *    pending_review, items 'approved'). Su trabajo distintivo es CODIFICAR
 *    (asignar Tarea/Código GL + Centro de Costo) y luego aprobar/devolver/rechazar
 *    su sección del recibo (L1). Las acciones son por SECCIÓN, no por partida.
 *  - NÓMINA: es el aprobador de Nivel 1 de las tarjetas ('submitted', vía ganga).
 *    Aprobar avanza: 2 niveles → Revisión Final; 3 niveles → Gerente. Devolver →
 *    al Supervisor(a) creador. El Ingeniero SÍ ve la validación AMGtime.
 * Contenido derivado de: receiptController, SupervisorDetailPage, nominaController
 * (approveSheet/returnSheet/submitSheet), TarjetaDetailPage.
 */
export default {
  roleKey: 'engineer',
  roleLabel: 'Ingeniero(a)',
  icon: '✅',
  subtitle: 'Gastos — someter, codificar y aprobar (L1). Nómina — aprobar (L1).',
  sections: [
    {
      id: 'someter-gasto',
      group: 'Gastos',
      title: 'Cómo someter un gasto',
      blocks: [
        { type: 'paragraph', text: 'Usted puede someter recibos igual que cualquier usuario.' },
        { type: 'steps', items: [
          'Abra "Gastos" → "Mis Recibos" y pulse "+ Nuevo Gasto".',
          'Suba la foto o el PDF; la IA autocompleta proveedor, fecha, subtotal y partidas. Revise que coincidan.',
          'Complete los datos del recibo y, en cada partida, el Proyecto, la Descripción y el Monto.',
          'Pulse "Enviar Reporte".',
        ] },
        { type: 'note', variant: 'warning', text: 'Cuando usted somete su PROPIO recibo, este NO pasa por la revisión del Ingeniero (Nivel 1) —usted no puede aprobar su propio gasto—: va directo a Revisión Final. Por eso no lo verá en su cola de "Recibos por Aprobar".' },
      ],
    },
    {
      id: 'codificar-gasto',
      group: 'Gastos',
      title: 'Cómo codificar un gasto (Código/GL) en revisión',
      blocks: [
        { type: 'paragraph', text: 'Codificar es su trabajo distintivo: después de que un Chofer (o Supervisor) somete un recibo, este llega a su cola y usted le asigna los códigos antes de aprobarlo.' },
        { type: 'steps', items: [
          'Abra "Gastos" → "Recibos por Aprobar" y seleccione el recibo.',
          'Para partidas de proyecto: asigne la "Tarea" (código de costo) y el "Centro de Costo".',
          'Para partidas administrativas: asigne el "Código GL".',
          'Puede ajustar en línea la descripción, el monto, la cantidad, la unidad y el impuesto de cada partida.',
        ] },
        { type: 'note', variant: 'info', text: 'Solo el Ingeniero(a), el Gerente y Revisión Final pueden codificar (asignar Tarea/GL y Centro de Costo).' },
        { type: 'note', variant: 'warning', text: 'No podrá aprobar si falta algo: cada partida de proyecto necesita Proyecto, Tarea y Centro de Costo; cada partida administrativa necesita Código GL. El sistema le dirá exactamente qué partida está incompleta.' },
      ],
    },
    {
      id: 'aprobar-partidas',
      group: 'Gastos',
      title: 'Aprobar, devolver o rechazar (por sección)',
      blocks: [
        { type: 'paragraph', text: 'Usted revisa solo SU sección del recibo: las partidas asignadas a usted aparecen como "Mis Items". Las partidas de otros supervisores aparecen aparte, en modo solo lectura. Sus acciones se aplican a toda su sección a la vez.' },
        { type: 'steps', items: [
          'Revise y codifique sus partidas.',
          '"Aprobar Mi Sección" — aprueba todas sus partidas pendientes (el recibo pasa a Revisión Final cuando todas las secciones están aprobadas).',
          '"Devolver Mi Sección" — la regresa al Chofer para corregir; debe indicar un motivo y notas.',
          '"Rechazar Mi Sección" — la rechaza con un motivo (no es elegible para reembolso).',
        ] },
        { type: 'subheading', text: 'Recibos de varios proyectos / supervisores' },
        { type: 'note', variant: 'warning', text: 'En un recibo que abarca varios proyectos o supervisores, su acción afecta ÚNICAMENTE su sección. Las secciones de los demás siguen su propio curso. Mientras unas secciones estén aprobadas y otras no, el recibo aparece como "Parcialmente Aprobado" o "Parcialmente Devuelto" hasta que todas se resuelvan.' },
      ],
    },
    {
      id: 'acciones-masivas',
      group: 'Gastos',
      title: 'Dividir partidas y validaciones al aprobar',
      blocks: [
        { type: 'subheading', text: 'Dividir una partida' },
        { type: 'steps', items: [
          'Pulse "Dividir item" en una partida para repartirla en varias (por ejemplo, entre distintas tareas o centros de costo).',
          'Los montos de las partidas divididas deben sumar exactamente el monto original.',
        ] },
        { type: 'subheading', text: 'Validaciones al aprobar su sección' },
        { type: 'list', items: [
          'Toda partida pendiente debe tener Proyecto, Tarea (o Código GL) y Centro de Costo.',
          'El total de sus items debe coincidir con su "Porción Asignada" (Total del Recibo − total de los otros supervisores), con tolerancia de $0.02.',
          'Si dividió partidas, cada grupo debe sumar el monto original.',
        ] },
        { type: 'note', variant: 'info', text: 'Puede aplicar el mismo impuesto (Tax) a todas sus partidas con la casilla correspondiente para no fijarlo una por una.' },
      ],
    },
    {
      id: 'crear-tarjeta',
      group: 'Nómina',
      title: 'Crear una tarjeta (envío inteligente salta el L1)',
      blocks: [
        { type: 'paragraph', text: 'Usted también puede crear tarjetas de tiempo (igual que un Supervisor): en "Horas", abra el proyecto, elija la fecha y pulse "Nueva Tarjeta", agregue trabajadores, tareas y horas (R / E).' },
        { type: 'note', variant: 'info', text: 'Al someter una tarjeta que usted mismo creó, el envío inteligente omite su propio nivel (L1): pasa al Gerente en proyectos de 3 niveles, o a Revisión Final en proyectos de 2 niveles.' },
      ],
    },
    {
      id: 'revisar-aprobar-l1',
      group: 'Nómina',
      title: 'Revisar y aprobar tarjetas (L1)',
      blocks: [
        { type: 'paragraph', text: 'Usted es el aprobador de Nivel 1 de las tarjetas de sus proyectos (las que están en estado "Pendiente Ingeniero"), según la ganga del proyecto.' },
        { type: 'steps', items: [
          'En "Horas", abra una tarjeta pendiente de su proyecto (se abre en modo revisión).',
          'Revise las horas de cada trabajador (regulares "R" y extra "E") y sus tareas.',
          'Use la barra "AMGtime" para comparar las horas de la tarjeta con los ponches reales; pulse "Refrescar" si necesita actualizarlas.',
          'Si todo está correcto, pulse "Aprobar".',
        ] },
        { type: 'note', variant: 'info', text: 'Al aprobar, la tarjeta avanza: en proyectos de 3 niveles pasa al Gerente; en proyectos de 2 niveles pasa directo a Revisión Final.' },
        { type: 'note', variant: 'info', text: 'Indicadores AMGtime: ✓ verde = las horas coinciden con el ponche; ámbar = hay diferencia; "Sin ponche" = el trabajador no tiene registro ese día. Úselos como guía antes de aprobar.' },
      ],
    },
    {
      id: 'devolver-tarjeta',
      group: 'Nómina',
      title: 'Devolver una tarjeta',
      blocks: [
        { type: 'steps', items: [
          'En la tarjeta en revisión, pulse "Devolver".',
          'Escriba la razón de la devolución (es obligatoria) y las notas necesarias.',
          'La tarjeta regresa al Supervisor(a) que la creó para que la corrija y la reenvíe.',
        ] },
        { type: 'note', variant: 'warning', text: 'No puede devolver una tarjeta que usted mismo creó. La razón de devolución es obligatoria.' },
      ],
    },
  ],
};
